import type {
  DoraDerivation,
  Metric,
  DeployFrequencyValue,
  LeadTimeForChangeValue,
  ChangeFailureRateValue,
  TimeToRestoreValue,
} from "./projectMetrics.js";

/**
 * E8.4 (NX-2631) — blends the E5.2 DORA metrics of a program's sub-projects into one program-level
 * figure per metric.
 *
 * This is NOT the same problem `packages/core/src/rollup.ts` (E5.4) solves. That module composes
 * DEPENDENT durations through a dependency DAG to answer "when does the whole program finish" — a
 * critical-path question, where max()/mean() over already-computed percentiles is provably wrong
 * (see rollup.ts's own header comment). Blending DORA across a program's sub-projects is a different,
 * simpler question: sub-projects are PARALLEL, independent delivery streams, not sequenced stages of
 * one critical path, so there is no dependency graph to walk and E5.4 has nothing to contribute here.
 * What this module does is the ordinary portfolio-aggregate operation — sum the additive counts,
 * take a sample-size-weighted mean of the durations/rates, recompute the ratio from the summed
 * numerator/denominator rather than averaging an average — over data GET /projects/:id already
 * returns for every sub-project (E5.2, landed).
 *
 * Honesty rules mirror dora.ts exactly (ADR-016): a sub-project whose own metric is `n/a` is EXCLUDED
 * from the blend rather than treated as a zero, and every blended value reports
 * `subProjectsObserved`/`subProjectsTotal` so a partial blend can never read as if every sub-project
 * contributed. If NO sub-project has an observed value for a metric, the blend is the honest `n/a` —
 * with the REAL reason (how many sub-projects were offered and had nothing to contribute), never the
 * stale "E5.2/E5.4 hasn't landed" reason the original ticket text used to give (both landed; see
 * NX-2631 PR description).
 */

type ValueMetric<T> = Extract<Metric<T>, { kind: "value" }>;
type MetricConfidence = ValueMetric<unknown>["confidence"];

export interface BlendedCoverage {
  /** Sub-projects whose own metric was a real value and fed this blend. */
  readonly subProjectsObserved: number;
  /** Sub-projects offered to the blend at all (observed or not) — the blend's true denominator. */
  readonly subProjectsTotal: number;
}
export interface BlendedDeployFrequencyValue extends BlendedCoverage {
  readonly deployments: number;
  readonly windowSeconds: number;
  readonly perDay: number;
}
export interface BlendedLeadTimeForChangeValue extends BlendedCoverage {
  readonly n: number;
  readonly meanSeconds: number;
}
export interface BlendedChangeFailureRateValue extends BlendedCoverage {
  readonly succeeded: number;
  readonly failed: number;
  readonly failureRate: number;
}
export interface BlendedTimeToRestoreValue extends BlendedCoverage {
  readonly n: number;
  readonly meanSeconds: number;
}
export interface BlendedDora {
  readonly deployFrequency: Metric<BlendedDeployFrequencyValue>;
  readonly leadTimeForChange: Metric<BlendedLeadTimeForChangeValue>;
  readonly changeFailureRate: Metric<BlendedChangeFailureRateValue>;
  readonly timeToRestore: Metric<BlendedTimeToRestoreValue>;
}

/** What the blend needs from each sub-project — structurally satisfied by the full project view. */
export interface DoraBearing {
  readonly dora: DoraDerivation;
}

const DAY_SECONDS = 86_400;

const isValue = <T>(m: Metric<T>): m is ValueMetric<T> => m.kind === "value";

/** Most-conservative confidence across the contributing sub-projects — same rule portfolio.ts's
 *  `envelopeConfidence` uses for a rolled-up figure: the blend is only as confident as its weakest
 *  contributor, never an average that could hide a shaky one. */
function blendConfidence(
  values: readonly { readonly confidence: MetricConfidence }[],
): MetricConfidence {
  const confidences = values
    .map((v) => (v.confidence.kind === "value" ? v.confidence.confidence : null))
    .filter((c): c is number => c !== null);
  return confidences.length === 0
    ? { kind: "n/a" }
    : { kind: "value", confidence: Math.min(...confidences) };
}

function blendLineage(
  values: readonly { readonly lineage: readonly number[] }[],
): readonly number[] {
  return [...new Set(values.flatMap((v) => v.lineage))].sort((a, b) => a - b);
}

/** Sample-size-weighted mean — a sub-project with more correlated samples counts for more, rather
 *  than every sub-project's mean counting equally regardless of how much evidence backs it. */
function weightedMean(
  pairs: readonly { readonly value: number; readonly weight: number }[],
): number {
  const totalWeight = pairs.reduce((sum, p) => sum + p.weight, 0);
  if (totalWeight === 0) return 0;
  return pairs.reduce((sum, p) => sum + p.value * p.weight, 0) / totalWeight;
}

const naReason = (total: number, metricLabel: string, none: string, unobserved: string): string =>
  total === 0
    ? `this program has no sub-projects — ${none}`
    : `none of the ${total} sub-project${total === 1 ? "" : "s"} has an observed ${metricLabel} — ${unobserved}`;

export function blendDeployFrequency(
  subs: readonly DoraBearing[],
): Metric<BlendedDeployFrequencyValue> {
  const values = subs.map((s) => s.dora.deployFrequency).filter(isValue<DeployFrequencyValue>);
  if (values.length === 0) {
    return {
      kind: "n/a",
      reason: naReason(
        subs.length,
        "deploy frequency",
        "blended deploy frequency cannot be computed from zero sub-projects",
        "blended deploy frequency is unobserved, which is not the same as a frequency of zero",
      ),
    };
  }
  const deployments = values.reduce((sum, v) => sum + v.value.deployments, 0);
  // All sub-projects derive over the same DEFAULT_DORA_CONFIG window at the same asOf, so their
  // windowSeconds should already agree; max() is a defensive fallback if a sub-project's window ever
  // legitimately differs (e.g. mid-migration), rather than the blend silently picking an arbitrary one.
  const windowSeconds = Math.max(...values.map((v) => v.value.windowSeconds));
  return {
    kind: "value",
    value: {
      subProjectsObserved: values.length,
      subProjectsTotal: subs.length,
      deployments,
      windowSeconds,
      perDay: windowSeconds > 0 ? deployments / (windowSeconds / DAY_SECONDS) : 0,
    },
    confidence: blendConfidence(values),
    lineage: blendLineage(values),
  };
}

export function blendLeadTimeForChange(
  subs: readonly DoraBearing[],
): Metric<BlendedLeadTimeForChangeValue> {
  const values = subs.map((s) => s.dora.leadTimeForChange).filter(isValue<LeadTimeForChangeValue>);
  if (values.length === 0) {
    return {
      kind: "n/a",
      reason: naReason(
        subs.length,
        "lead time for change",
        "blended lead time for change cannot be computed from zero sub-projects",
        "blended lead time for change is unobserved, which is not the same as an instant lead time",
      ),
    };
  }
  const n = values.reduce((sum, v) => sum + v.value.n, 0);
  const meanSeconds = weightedMean(
    values.map((v) => ({ value: v.value.meanSeconds, weight: v.value.n })),
  );
  return {
    kind: "value",
    value: { subProjectsObserved: values.length, subProjectsTotal: subs.length, n, meanSeconds },
    confidence: blendConfidence(values),
    lineage: blendLineage(values),
  };
}

export function blendChangeFailureRate(
  subs: readonly DoraBearing[],
): Metric<BlendedChangeFailureRateValue> {
  const values = subs.map((s) => s.dora.changeFailureRate).filter(isValue<ChangeFailureRateValue>);
  if (values.length === 0) {
    return {
      kind: "n/a",
      reason: naReason(
        subs.length,
        "change-failure rate",
        "blended change-failure rate cannot be computed from zero sub-projects",
        "blended change-failure rate is unobserved, which is not the same as a rate of zero",
      ),
    };
  }
  const succeeded = values.reduce((sum, v) => sum + v.value.succeeded, 0);
  const failed = values.reduce((sum, v) => sum + v.value.failed, 0);
  const decided = succeeded + failed;
  return {
    kind: "value",
    value: {
      subProjectsObserved: values.length,
      subProjectsTotal: subs.length,
      succeeded,
      failed,
      // Recomputed from the SUMMED counts, never an average of each sub-project's own rate — an
      // average-of-rates would weight a 1-deploy sub-project the same as a 100-deploy one.
      failureRate: decided > 0 ? failed / decided : 0,
    },
    confidence: blendConfidence(values),
    lineage: blendLineage(values),
  };
}

export function blendTimeToRestore(
  subs: readonly DoraBearing[],
): Metric<BlendedTimeToRestoreValue> {
  const values = subs.map((s) => s.dora.timeToRestore).filter(isValue<TimeToRestoreValue>);
  if (values.length === 0) {
    return {
      kind: "n/a",
      reason: naReason(
        subs.length,
        "time to restore",
        "blended time to restore cannot be computed from zero sub-projects",
        "blended time to restore is unobserved, which is not the same as instant recovery",
      ),
    };
  }
  const n = values.reduce((sum, v) => sum + v.value.n, 0);
  const meanSeconds = weightedMean(
    values.map((v) => ({ value: v.value.meanSeconds, weight: v.value.n })),
  );
  return {
    kind: "value",
    value: { subProjectsObserved: values.length, subProjectsTotal: subs.length, n, meanSeconds },
    confidence: blendConfidence(values),
    lineage: blendLineage(values),
  };
}

export function blendDora(subs: readonly DoraBearing[]): BlendedDora {
  return {
    deployFrequency: blendDeployFrequency(subs),
    leadTimeForChange: blendLeadTimeForChange(subs),
    changeFailureRate: blendChangeFailureRate(subs),
    timeToRestore: blendTimeToRestore(subs),
  };
}
